import React, { useState, useEffect } from 'react';
import { FaEllipsisV, FaSearch, FaEye, FaTrash, FaTimes } from 'react-icons/fa';
import axios from 'axios';

const OrderModal = ({ order, onClose, onUpdateStatus }) => {
  if (!order) return null;

  const handleChangeStatus = async (newStatus) => {
    if (newStatus !== order.orderStatus) {
      await onUpdateStatus(order._id, newStatus);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-11/12 max-w-4xl relative max-h-[90vh] flex flex-col">
        {/* Header - Fixed at top */}
        <div className="sticky top-0 bg-white p-6 border-b rounded-t-lg z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Order Details</h2>
            <button 
              onClick={onClose} 
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <FaTimes size={24} />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto p-6 flex-1">
          {/* Order Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="font-semibold mb-2">Order Information</h3>
              <p>Order Number: #{order.orderNumber}</p>
              <p >OrderType: {order.orderType}</p>
              <p>Date: {new Date(order.date).toLocaleString()}</p>
              <p>Total Amount: ${order.total}</p>
              <p>
                Status:{' '}
                <select
                  value={order.orderStatus}
                  onChange={(e) => handleChangeStatus(e.target.value)}
                  className="px-2 py-1 rounded border"
                >
                  <option value="shipped">Shipped</option>
                  <option value="completed">Completed</option>
                </select>
              </p>
              <p>Shipping Method: {order.shippingMethod}</p>
              <p>Payment Method: {order.paymentMethod}</p>
              {order.deliveryTime && <p>Delivery Time: {order.deliveryTime}</p>}
            </div>

            <div>
              <h3 className="font-semibold mb-2">Billing Address</h3>
              <p>
                {order.billingAddress.firstName} {order.billingAddress.lastName}
              </p>
              <p>{order.billingAddress.streetHouseApartment}</p>
              {order.billingAddress.apartment && (
                <p>{order.billingAddress.apartment}</p>
              )}
              <p>
                {order.billingAddress.city}, {order.billingAddress.state}{' '}
                {order.billingAddress.zipCode}
              </p>
              <p>{order.billingAddress.country}</p>
              <p>Phone: {order.billingAddress.phone}</p>
              <p>Email: {order.billingAddress.email}</p>
            </div>
          </div>

          {/* Order Items */}
          <div>
            <h3 className="font-semibold mb-4">Order Items</h3>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="p-2 text-left">Product</th>
                    <th className="p-2 text-left">Brand</th>
                    <th className="p-2 text-left">Price</th>
                    <th className="p-2 text-left">Quantity</th>
                    <th className="p-2 text-left">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {order.cartItems.map((item, index) => (
                    <tr key={index} className="border-b">
                      <td className="p-2">
                        <div className="flex items-center">
                          <img
                            src={item.img}
                            alt={item.name}
                            className="w-12 h-12 object-cover rounded mr-2"
                          />
                          <span>{item.name}</span>
                        </div>
                      </td>
                      <td className="p-2">{item.brand}</td>
                      <td className="p-2">${item.price}</td>
                      <td className="p-2">{item.quantity}</td>
                      <td className="p-2">
                        ${parseFloat(item.price) * item.quantity}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const token = localStorage.getItem("adminToken");


  const fetchOrders = async () => {
    try {
      const response = await axios.get(
        "https://afric-fresh-backend.onrender.com/admin/orders"
        ,{
          headers: {
            Authorization: `Bearer ${token}`
          }}
      );
      setOrders(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setLoading(false);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (window.confirm('Are you sure you want to delete this order?')) {
      try {
        await axios.delete(
          `https://afric-fresh-backend.onrender.com/admin/orders/${orderId}`
          ,{
            headers: {
              Authorization: `Bearer ${token}`
            }});
        fetchOrders();
      } catch (error) {
        console.error('Error deleting order:', error);
      }
    }
  };

  const filterOrders = () => {
    return orders.filter(order => {
      const matchesSearch = (
        order.orderNumber.toString().includes(searchTerm) ||
        order.billingAddress.email.toLowerCase().includes(searchTerm.toLowerCase())
      );

      const matchesTab = 
        activeTab === 'All' || 
        (activeTab === 'Shipped' && order.orderStatus === 'shipped') ||
        (activeTab === 'Completed' && order.orderStatus === 'completed');

      return matchesSearch && matchesTab;
    });
  };

  const ordersPerPage = 8;
  const filteredOrders = filterOrders();
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const tabs = ['All',  'Shipped', 'Completed'];

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(
        `https://afric-fresh-backend.onrender.com/admin/orders/${orderId}`,
        { orderStatus: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  useEffect(() => {
    fetchOrders();
    markOrdersAsRead(); // Add this line
  }, []);

  // Add this function
  const markOrdersAsRead = async () => {
    try {
      await axios.put(
        "https://afric-fresh-backend.onrender.com/order/mark-orders-read",
        {}, // Empty body if no data needs to be sent
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
    } catch (error) {
      console.error('Error marking orders as read:', error);
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Header Section */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold mb-6">Orders Management</h1>
          
          {/* Search Bar */}
          <div className="flex justify-between items-center mb-6">
            <div className="relative w-96">
              <input 
                type="text" 
                placeholder="Search by Order ID or Email..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border px-4 py-2 rounded-lg pl-10"
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 border-b">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 ${
                  activeTab === tab
                    ? 'border-b-2 border-blue-500 text-blue-500'
                    : 'text-gray-500'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Orders Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="p-3 text-left">Order ID</th>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">Customer</th>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left">OrderType</th>
                <th className="p-3 text-left">Delivery Time/Type</th>
                <th className="p-3 text-left">Total</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center p-4">Loading...</td>
                </tr>
              ) : currentOrders.map((order) => (
                <tr key={order._id} className="border-b hover:bg-gray-50">
                  <td className="p-3">#{order.orderNumber}</td>
                  <td className="p-3">{new Date(order.date).toLocaleDateString()}</td>
                  <td className="p-3">
                    {order.billingAddress.firstName} {order.billingAddress.lastName}
                  </td>
                  <td className="p-3">{order.billingAddress.email}</td>
                  <td className="p-3">{order.orderType}</td>
                  <td className="p-3">{order.deliveryTime}</td>
                  <td className="p-3">${order.total}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      order.orderStatus === 'completed' ? 'bg-green-100 text-green-800' :
                      order.orderStatus === 'processed' ? 'bg-blue-100 text-blue-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {order.orderStatus}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleViewOrder(order)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <FaEye />
                      </button>
                      <button 
                        onClick={() => handleDeleteOrder(order._id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-6">
          <span className="text-gray-600">
            Showing {indexOfFirstOrder + 1} to {Math.min(indexOfLastOrder, filteredOrders.length)} of {filteredOrders.length} entries
          </span>
          <div className="flex space-x-2">
            <button 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-4 py-2 border rounded-lg disabled:opacity-50"
            >
              Previous
            </button>
            {[...Array(totalPages)].map((_, index) => (
              <button
                key={index}
                onClick={() => handlePageChange(index + 1)}
                className={`
                  px-4 py-2 border rounded-lg 
                  ${currentPage === index + 1 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-white text-gray-700'
                  }
                `}
              >
                {index + 1}
              </button>
            ))}
            <button 
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border rounded-lg disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {selectedOrder && (
        <OrderModal 
          order={selectedOrder} 
          onClose={() => setSelectedOrder(null)} 
          onUpdateStatus={handleUpdateStatus} 
        />
      )}
    </div>
  );
};

export default AdminOrders;